import React, {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from "react";

const getModalElement = async (modalId) => {
  const existingModal = document.getElementById(modalId);
  if (
    existingModal &&
    (typeof existingModal.showOverlay === "function" ||
      typeof existingModal.hideOverlay === "function")
  ) {
    return existingModal;
  }

  if (typeof customElements !== "undefined" && !customElements.get("s-modal")) {
    await Promise.race([
      customElements.whenDefined("s-modal"),
      new Promise((resolve) => {
        setTimeout(resolve, 1000);
      }),
    ]);
  }

  return document.getElementById(modalId);
};

const normalizeActions = (actions) => {
  if (!actions) {
    return [];
  }
  return Array.isArray(actions) ? actions : [actions];
};

const getModalDialogElement = (modal) => {
  if (!modal?.shadowRoot) {
    return null;
  }

  return modal.shadowRoot.querySelector("dialog");
};

const getModalPanelElement = (dialog) => {
  if (!dialog) {
    return null;
  }

  return (
    dialog.querySelector(".has-dialog-overlay") ||
    dialog.querySelector('[part="panel"]') ||
    dialog.querySelector('[class*="Panel"]') ||
    dialog.firstElementChild
  );
};

const isPointerOutsidePanel = (event, dialog) => {
  const panel = getModalPanelElement(dialog);
  if (!panel) {
    return event.target === dialog;
  }

  const rect = panel.getBoundingClientRect();
  return !(
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
};

const isBackdropPointer = (event, dialog) => {
  if (!dialog) {
    return false;
  }

  if (isPointerInsideAnyPopover(event)) {
    return false;
  }

  return event.target === dialog || isPointerOutsidePanel(event, dialog);
};

const isPopoverTriggerPointer = (event, modalId) =>
  event.composedPath().some((node) => {
    if (!(node instanceof Element) || node.localName !== "s-button") {
      return false;
    }

    const commandFor =
      node.getAttribute("commandfor") || node.getAttribute("commandFor");
    if (!commandFor || commandFor === modalId) {
      return false;
    }

    const commandTarget = document.getElementById(commandFor);
    return (
      commandTarget?.localName === "s-popover" ||
      commandTarget?.localName === "s-tooltip"
    );
  });

const isPointerInsideAnyPopover = (event) => {
  if (!(event.target instanceof Element)) {
    return false;
  }

  if (event.target.closest("s-popover, s-tooltip")) {
    return true;
  }

  if (
    event.composedPath().some(
      (node) =>
        node instanceof Element &&
        (node.localName === "s-popover" || node.localName === "s-tooltip")
    )
  ) {
    return true;
  }

  return Array.from(document.querySelectorAll("s-popover, s-tooltip")).some(
    (overlay) => {
      const rect = overlay.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return false;
      }

      return (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      );
    }
  );
};

const isAnyPopoverVisible = () =>
  Array.from(document.querySelectorAll("s-popover, s-tooltip")).some(
    (overlay) => {
      const rect = overlay.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }
  );

const shouldDismissFromPointer = (event, modalId) => {
  if (isPointerInsideAnyPopover(event)) {
    return false;
  }

  // Only explicit close controls (X, Cancel, command="--hide") dismiss the modal.
  // Backdrop clicks are ignored.
  return isIntentionalModalCloseTarget(event, modalId);
};

const isIntentionalModalCloseTarget = (event, modalId) =>
  event.composedPath().some((node) => {
    if (!(node instanceof Element)) {
      return false;
    }

    const part = node.getAttribute?.("part");
    if (part === "close" || part === "close-button") {
      return true;
    }

    if (node.classList?.contains("close") || node.closest?.(".close")) {
      return true;
    }

    if (node.localName !== "s-button") {
      return false;
    }

    const slot = node.getAttribute("slot");
    if (slot === "secondary-actions") {
      return true;
    }

    const command = node.getAttribute("command");
    const commandFor =
      node.getAttribute("commandfor") || node.getAttribute("commandFor");

    return command === "--hide" && commandFor === modalId;
  });

/**
 * Polaris Modal compatible wrapper around Shopify App Home s-modal.
 * Popover / Tooltip inside modals: prefer s-popover / s-tooltip (commandFor)
 * over Polaris Popover; see shopify-s-modal-migration skill.
 */
const ShopifyModal = forwardRef(
  (
    {
      id,
      open = false,
      onClose,
      heading,
      title,
      size = "base",
      padding = "base",
      accessibilityLabel,
      primaryAction,
      secondaryActions,
      showCloseButton = true,
      closeText = "Close",
      children,
    },
    ref
  ) => {
    const generatedId = useId().replace(/:/g, "");
    const modalId = id || `shopify-modal-${generatedId}`;
    const modalRef = useRef(null);
    const suppressCloseSideEffects = useRef(false);
    const modalWasVisibleRef = useRef(false);
    const closedByUserRef = useRef(false);
    const isHandlingCloseRef = useRef(false);
    const lastPointerDownRef = useRef(null);
    const popoverDismissOnlyRef = useRef(false);
    const onCloseRef = useRef(onClose);

    onCloseRef.current = onClose;

    useEffect(() => {
      if (!open) {
        lastPointerDownRef.current = null;
        popoverDismissOnlyRef.current = false;
        return undefined;
      }

      let isActive = true;
      let detachBackdropGuard = () => {};

      const attachBackdropGuard = (modal) => {
        const dialog = getModalDialogElement(modal);
        if (!dialog) {
          return null;
        }

        const blockBackdropDismiss = (event) => {
          if (
            shouldDismissFromPointer(event, modalId) ||
            isPointerInsideAnyPopover(event) ||
            isPopoverTriggerPointer(event, modalId)
          ) {
            return;
          }

          if (!isBackdropPointer(event, dialog)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        };

        dialog.addEventListener("click", blockBackdropDismiss, true);
        return () => {
          dialog.removeEventListener("click", blockBackdropDismiss, true);
        };
      };

      getModalElement(modalId).then((modal) => {
        if (!modal || !isActive) {
          return;
        }

        detachBackdropGuard = attachBackdropGuard(modal) || (() => {});

        if (getModalDialogElement(modal)) {
          return;
        }

        const observer = new MutationObserver(() => {
          if (!isActive) {
            return;
          }

          if (!getModalDialogElement(modal)) {
            return;
          }

          detachBackdropGuard();
          detachBackdropGuard = attachBackdropGuard(modal) || (() => {});
          observer.disconnect();
        });

        if (modal.shadowRoot) {
          observer.observe(modal.shadowRoot, { childList: true, subtree: true });
        }

        const previousDetach = detachBackdropGuard;
        detachBackdropGuard = () => {
          observer.disconnect();
          previousDetach();
        };
      });

      const trackPointerDown = (event) => {
        const insidePopover = isPointerInsideAnyPopover(event);
        const popoverVisible = isAnyPopoverVisible();
        const popoverTrigger = isPopoverTriggerPointer(event, modalId);
        const dismiss = shouldDismissFromPointer(event, modalId);

        if (popoverTrigger || insidePopover || (popoverVisible && !dismiss)) {
          popoverDismissOnlyRef.current = true;
          window.setTimeout(() => {
            popoverDismissOnlyRef.current = false;
          }, 500);
        }

        lastPointerDownRef.current = {
          time: Date.now(),
          dismiss,
        };
      };

      document.addEventListener("pointerdown", trackPointerDown, true);
      return () => {
        isActive = false;
        detachBackdropGuard();
        document.removeEventListener("pointerdown", trackPointerDown, true);
        lastPointerDownRef.current = null;
      };
    }, [open, modalId]);

    useImperativeHandle(ref, () => ({
      hideOverlay: () => {
        if (typeof modalRef.current?.hideOverlay === "function") {
          modalRef.current.hideOverlay();
        }
      },
      showOverlay: () => {
        if (typeof modalRef.current?.showOverlay === "function") {
          modalRef.current.showOverlay();
        }
      },
      getElement: () => modalRef.current,
    }));

    useEffect(() => {
      let isActive = true;
      let modalElement = null;

      const handleHide = () => {
        const pointer = lastPointerDownRef.current;
        const isRecentPointer =
          pointer && Date.now() - pointer.time < 500;

        const shouldKeepOpen =
          popoverDismissOnlyRef.current ||
          (isRecentPointer && pointer.dismiss === false);

        if (shouldKeepOpen) {
          const modal = document.getElementById(modalId);
          if (modal && typeof modal.showOverlay === "function") {
            modal.showOverlay();
          }
          return;
        }

        lastPointerDownRef.current = null;
        popoverDismissOnlyRef.current = false;

        if (suppressCloseSideEffects.current) {
          suppressCloseSideEffects.current = false;
          return;
        }
        if (isHandlingCloseRef.current) {
          return;
        }
        isHandlingCloseRef.current = true;
        closedByUserRef.current = true;
        onCloseRef.current?.();
        isHandlingCloseRef.current = false;
      };

      getModalElement(modalId).then((el) => {
        if (!el || !isActive) {
          return;
        }
        modalElement = el;
        modalElement.addEventListener("hide", handleHide);
      });

      return () => {
        isActive = false;
        modalElement?.removeEventListener("hide", handleHide);
      };
    }, [modalId]);

    useEffect(() => {
      let isActive = true;

      if (!open && modalWasVisibleRef.current) {
        suppressCloseSideEffects.current = !closedByUserRef.current;
      }

      getModalElement(modalId).then((modal) => {
        if (!modal || !isActive) {
          return;
        }

        if (open) {
          modalWasVisibleRef.current = true;
          if (typeof modal.showOverlay === "function") {
            modal.showOverlay();
          }
        } else if (modalWasVisibleRef.current) {
          if (closedByUserRef.current) {
            closedByUserRef.current = false;
            return;
          }
          if (typeof modal.hideOverlay === "function") {
            modal.hideOverlay();
          }
        }
      });

      return () => {
        isActive = false;
      };
    }, [open, modalId]);

    const secondaryActionList = normalizeActions(secondaryActions);
    const shouldRenderDefaultClose =
      showCloseButton && secondaryActionList.length === 0;

    const modalHeading = heading || title;

    return (
      <s-modal
        id={modalId}
        ref={modalRef}
        heading={modalHeading}
        size={size}
        padding={padding}
        accessibilityLabel={accessibilityLabel}
      >
        {open ? (
          <div
            className="shopify-modal-content-shell"
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
          >
            {children}
          </div>
        ) : null}

        {secondaryActionList.map((action, index) => {
          const { content, onAction, disabled, loading, tone = "auto" } = action;

          if (onAction) {
            return (
              <s-button
                key={`secondary-${index}-${content}`}
                slot="secondary-actions"
                variant="secondary"
                tone={tone}
                disabled={disabled || undefined}
                loading={loading || undefined}
                onClick={onAction}
              >
                {content}
              </s-button>
            );
          }

          return (
            <s-button
              key={`secondary-${index}-${content}`}
              slot="secondary-actions"
              variant="secondary"
              tone={tone}
              disabled={disabled || undefined}
              loading={loading || undefined}
              commandFor={modalId}
              command="--hide"
            >
              {content}
            </s-button>
          );
        })}

        {shouldRenderDefaultClose && (
          <s-button
            slot="secondary-actions"
            commandFor={modalId}
            command="--hide"
          >
            {closeText}
          </s-button>
        )}

        {primaryAction && (
          <s-button
            slot="primary-action"
            variant="primary"
            tone={primaryAction.destructive ? "critical" : primaryAction.tone || "auto"}
            disabled={primaryAction.disabled || undefined}
            loading={primaryAction.loading || undefined}
            onClick={primaryAction.onAction}
          >
            {primaryAction.content}
          </s-button>
        )}
      </s-modal>
    );
  }
);

ShopifyModal.displayName = "ShopifyModal";

export default ShopifyModal;
